import { useCallback, useEffect, useRef, useState } from 'react'
import { removeTabCreatedAt, saveTabCreatedAt } from 'features/load-hub/utils/localStorage'
import { readPerWindow, readPerWindowScoped, writePerWindow, writePerWindowScoped } from 'features/load-hub/utils/perWindowStorage'
import { getToday } from 'features/load-hub/utils/date'
import { toDate } from '../utils/format'
import { BOX_TRUCK_WORKSPACE_TTL_MS, getExpiredBoxTruckTabIds } from '../utils/boxTruckTabExpiration'
import {
    BOX_TRUCK_DAT_SELECTION_VERSION,
    migrateBoxTruckEquipmentLabel,
    restoreBoxTruckDatSubtypeCodes,
    sanitizeBoxTruckCapacityLimits,
    sanitizeBoxTruckDatSubtypeCodes,
    sanitizeBoxTruckMaxPostedAgeHours,
} from '@numeo/types-core'
import {
    BoxTruckSearchFilters,
    capOrNull,
    EMPTY_FILTERS,
    getDefaultPickupEnd,
    legOptions,
    loadTypeOrBoth,
    sanitizePickup,
    sanitizePickupEnd,
} from './requestMapping'

/** One search tab: its (last-committed) filters + whether it has run a search yet. A not-yet-searched tab
 *  renders the "New search" empty state instead of a board — the board NEVER auto-runs an anywhere search. */
export interface BoxTruckTab {
    id: string
    filters: BoxTruckSearchFilters
    searched: boolean
    /** Custom tab name — set automatically to the driver's name on "Search for driver" and editable by the
     *  operator (double-click the tab). Absent → the tab labels itself by its searched lane. */
    label?: string
}

/** A fresh tab starts on the required today .. today+6 pickup window (mirrors the filter default),
 *  anywhere-to-anywhere, unsearched. */
const freshFilters = (): BoxTruckSearchFilters => ({ ...EMPTY_FILTERS, startDate: getToday(), endDate: getDefaultPickupEnd() })

const newTabId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`

// localStorage persistence (mirrors Load Hub): the tab set + active tab survive a reload. The seven-day purge
// reuses Load Hub's shared tab-created-at helpers against this box-truck-scoped key.
const STORAGE_KEY = 'box_truck_tabs'
const TAB_CREATED_KEY = 'box_truck_tab_created'

const serialize = (f: BoxTruckSearchFilters) => ({
    origin: f.origin ?? null,
    destination: f.destination ?? null,
    originOptions: f.originOptions ?? [],
    destinationOptions: f.destinationOptions ?? [],
    originRadius: f.originRadius ?? null,
    destinationRadius: f.destinationRadius ?? null,
    startDate: f.startDate ? f.startDate.toISOString() : null,
    endDate: f.endDate ? f.endDate.toISOString() : null,
    equipmentLabels: f.equipmentLabels ?? [],
    datEquipmentTypes: sanitizeBoxTruckDatSubtypeCodes(f.datEquipmentTypes),
    datEquipmentSelectionVersion: BOX_TRUCK_DAT_SELECTION_VERSION,
    capacityLimits: sanitizeBoxTruckCapacityLimits(f.capacityLimits),
    includeUnknownCapacity: f.includeUnknownCapacity !== false,
    fullPartial: f.fullPartial ?? 'BOTH',
    lengthFt: f.lengthFt ?? null,
    weightLbs: f.weightLbs ?? null,
    maxPostedAgeHours: sanitizeBoxTruckMaxPostedAgeHours(f.maxPostedAgeHours),
})

const deserialize = (p: ReturnType<typeof serialize>): BoxTruckSearchFilters => ({
    origin: p.origin ?? null,
    destination: p.destination ?? null,
    originOptions: legOptions(p.originOptions, p.origin ?? null),
    destinationOptions: legOptions(p.destinationOptions, p.destination ?? null),
    originRadius: p.originRadius ?? null,
    destinationRadius: p.destinationRadius ?? null,
    // Snap a restored-stale (past) pickup to today — same required-pickup invariant as useBoxTruckFilterPersistence,
    // so a tab persisted yesterday can't re-run its mount search with a stale past window. An unset end defaults to
    // the today+6 window.
    startDate: sanitizePickup(toDate(p.startDate)),
    endDate: sanitizePickupEnd(toDate(p.endDate)),
    // Same migration as useBoxTruckFilterPersistence, and for the same reason: a label that no longer exists
    // does not degrade to "no filter", it matches no row and no DAT code. Tabs are the PRIMARY restore path
    // (they own the committed filters), so a stale one here is an empty board under a normal-looking chip.
    equipmentLabels: Array.isArray(p.equipmentLabels) ? p.equipmentLabels.map(migrateBoxTruckEquipmentLabel) : [],
    datEquipmentTypes: restoreBoxTruckDatSubtypeCodes(p.datEquipmentTypes, p.datEquipmentSelectionVersion),
    capacityLimits: sanitizeBoxTruckCapacityLimits(p.capacityLimits),
    includeUnknownCapacity: p.includeUnknownCapacity !== false,
    fullPartial: loadTypeOrBoth(p.fullPartial),
    lengthFt: capOrNull(p.lengthFt),
    weightLbs: capOrNull(p.weightLbs),
    maxPostedAgeHours: sanitizeBoxTruckMaxPostedAgeHours(p.maxPostedAgeHours),
    numeoId: null,
})

const readPersisted = (scopedOnly = false): { tabs: BoxTruckTab[]; activeTabId: string } | null => {
    try {
        const raw = scopedOnly ? readPerWindowScoped(STORAGE_KEY) : readPerWindow(STORAGE_KEY)
        if (!raw) return null
        const p = JSON.parse(raw)
        if (!p || !Array.isArray(p.tabs) || p.tabs.length === 0) return null
        const all: BoxTruckTab[] = p.tabs.map((t: { id: string; filters: ReturnType<typeof serialize>; searched: boolean; label?: unknown }) => ({
            id: String(t.id),
            filters: deserialize(t.filters ?? ({} as ReturnType<typeof serialize>)),
            searched: t.searched === true,
            label: typeof t.label === 'string' && t.label.trim() ? t.label : undefined,
        }))
        // Drop tabs past the Box Truck seven-day TTL (or with a missing created-at).
        // Also delete each expired tab's created-at entry (not just filter the tab out), or box_truck_tab_created
        // leaks a stale timestamp per expired tab forever — mirrors useTabManager's purge loop.
        const expiredIds = getExpiredBoxTruckTabIds(
            all.map((t) => t.id),
            TAB_CREATED_KEY
        )
        for (const id of expiredIds) removeTabCreatedAt(id, TAB_CREATED_KEY)
        const expired = new Set(expiredIds)
        const tabs = all.filter((t) => !expired.has(t.id))
        if (tabs.length === 0) return null
        const activeTabId = tabs.some((t) => t.id === p.activeTabId) ? p.activeTabId : tabs[0].id
        return { tabs, activeTabId }
    } catch {
        return null
    }
}

const writePersisted = (tabs: BoxTruckTab[], activeTabId: string, scopedOnly = false): void => {
    try {
        const write = scopedOnly ? writePerWindowScoped : writePerWindow
        write(
            STORAGE_KEY,
            JSON.stringify({ tabs: tabs.map((t) => ({ id: t.id, filters: serialize(t.filters), searched: t.searched, label: t.label })), activeTabId }),
            BOX_TRUCK_WORKSPACE_TTL_MS
        )
    } catch {
        // Storage disabled / private mode — persistence is best-effort.
    }
}

interface BoxTruckTabsApi {
    tabs: BoxTruckTab[]
    activeTabId: string
    activeTab: BoxTruckTab
    addTab: () => void
    removeTab: (id: string) => void
    closeAll: () => void
    switchTab: (id: string) => void
    /** Persist active-tab filters; Search marks the tab searched, while Clear preserves its current status. */
    commitFilters: (filters: BoxTruckSearchFilters, markSearched: boolean) => void
    /** Reorder to the given id order (tab-strip drag & drop); unknown ids ignored, leftovers appended. */
    reorderTabs: (orderedIds: string[]) => void
    /** Set/clear a tab's custom name (driver-select auto-name + manual rename). '' clears back to the lane label. */
    renameTab: (id: string, label: string) => void
}

interface BoxTruckIsolatedTabSeed {
    /** A browser-tab handoff was requested. Even when `tab` is absent/invalid, do not clone the source
     *  window's entire persisted tab set into this new top-level browser tab. */
    requested: boolean
    /** True for both an initial handoff and later reloads of that same detached top-level window. */
    isolated: boolean
    tab?: Omit<BoxTruckTab, 'id'>
    /** False while the target is still validating/switching identity; prevents wrong-company state writes. */
    persist?: boolean
}

/**
 * Multi-search tabs for the Box Truck loadboard — the same model as Load Hub: the board opens on a single
 * UNSEARCHED tab (a "New search" empty state, no request fired), and each tab holds its own last-searched lane +
 * searched flag. The tab set persists across reload (seven-day TTL, reusing Load Hub's tab-created-at purge helpers).
 * `seedFilters` pre-fills the FIRST tab's draft from the restored last search, still unsearched — so no anywhere
 * query on mount.
 */
export const useBoxTruckTabs = (
    seedFilters: BoxTruckSearchFilters,
    deepLink?: BoxTruckSearchFilters | null,
    deepLinkLabel?: string | null,
    isolatedSeed?: BoxTruckIsolatedTabSeed
): BoxTruckTabsApi => {
    const isolatedWindow = isolatedSeed?.isolated === true

    const [state, setState] = useState<{ tabs: BoxTruckTab[]; activeTabId: string }>(() => {
        // A top-level-browser-tab handoff always starts as ONE isolated Numeo tab. It must take precedence
        // over readPersisted(): a new browser tab otherwise falls back to the bare localStorage mirror and
        // clones every internal tab from the source window. A missing/invalid handoff degrades to one fresh,
        // unsearched tab rather than exposing some unrelated window's searches.
        if (isolatedSeed?.requested) {
            const id = newTabId()
            const tab = isolatedSeed.tab
            return {
                tabs: [{ id, filters: tab?.filters ?? freshFilters(), searched: tab?.searched === true, label: tab?.label }],
                activeTabId: id,
            }
        }
        // A detached window may restore only its OWN previous state. Falling back to the shared bare mirror
        // here would clone the source window after a reload or recovery navigation.
        const restored = readPersisted(isolatedWindow)
        // A URL deep-link (Map "Find loads" / state "View table") opens a dedicated, already-searched tab ON TOP
        // of any restored tabs and activates it — so the operator's existing tabs are preserved and the
        // driver/state search runs immediately on mount (the page's mount effect fires it for a `searched` tab).
        if (deepLink) {
            const id = newTabId()
            saveTabCreatedAt(id, TAB_CREATED_KEY)
            // The Map "Find loads" deep-link carries the driver's name — the tab opens already named after them.
            const dlTab: BoxTruckTab = { id, filters: deepLink, searched: true, label: deepLinkLabel?.trim() || undefined }
            return restored ? { tabs: [...restored.tabs, dlTab], activeTabId: id } : { tabs: [dlTab], activeTabId: id }
        }
        if (restored) return restored
        const id = newTabId()
        saveTabCreatedAt(id, TAB_CREATED_KEY)
        return { tabs: [{ id, filters: seedFilters, searched: false }], activeTabId: id }
    })
    // Mirror state so the handlers can read the current tabs synchronously (for the close-the-last-tab case).
    const stateRef = useRef(state)
    stateRef.current = state
    // Stamp isolated tabs from the effect, not the useState initializer: React Strict Mode deliberately
    // invokes initializers twice in development and would otherwise leave an orphan created-at id behind.
    const pendingCreatedAtRef = useRef(isolatedSeed?.requested === true)

    // Persist the tab set (+ active) on every change so a reload restores them within seven days.
    useEffect(() => {
        if (isolatedSeed?.persist === false) return
        // Auth may finish hydrating without a reload. Stamp the initially deferred tab immediately before
        // its first write so the normal missing-created-at purge cannot discard it on the next refresh.
        if (pendingCreatedAtRef.current) {
            state.tabs.forEach((tab) => saveTabCreatedAt(tab.id, TAB_CREATED_KEY))
            pendingCreatedAtRef.current = false
        }
        writePersisted(state.tabs, state.activeTabId, isolatedWindow)
    }, [isolatedSeed?.persist, isolatedWindow, state])

    const activeTab = state.tabs.find((t) => t.id === state.activeTabId) ?? state.tabs[0]

    const addTab = useCallback(() => {
        const id = newTabId()
        saveTabCreatedAt(id, TAB_CREATED_KEY)
        setState((s) => ({ tabs: [...s.tabs, { id, filters: freshFilters(), searched: false }], activeTabId: id }))
    }, [])

    const removeTab = useCallback((id: string) => {
        removeTabCreatedAt(id, TAB_CREATED_KEY)
        const { tabs, activeTabId } = stateRef.current
        const remaining = tabs.filter((t) => t.id !== id)
        // Never drop below one tab — closing the last one opens a fresh empty tab.
        if (remaining.length === 0) {
            const fresh = { id: newTabId(), filters: freshFilters(), searched: false }
            saveTabCreatedAt(fresh.id, TAB_CREATED_KEY)
            setState({ tabs: [fresh], activeTabId: fresh.id })
            return
        }
        setState({ tabs: remaining, activeTabId: activeTabId === id ? remaining[0].id : activeTabId })
    }, [])

    const closeAll = useCallback(() => {
        stateRef.current.tabs.forEach((t) => removeTabCreatedAt(t.id, TAB_CREATED_KEY))
        const fresh = { id: newTabId(), filters: freshFilters(), searched: false }
        saveTabCreatedAt(fresh.id, TAB_CREATED_KEY)
        setState({ tabs: [fresh], activeTabId: fresh.id })
    }, [])

    const switchTab = useCallback((id: string) => setState((s) => ({ ...s, activeTabId: id })), [])

    /** Reorder to the given id order (drag & drop in the tab strip). Unknown ids are ignored; tabs missing
     *  from the list keep their relative order at the end — a stale drop can never lose a tab. */
    const reorderTabs = useCallback((orderedIds: string[]) => {
        setState((s) => {
            const byId = new Map(s.tabs.map((t) => [t.id, t]))
            const reordered = orderedIds.map((id) => byId.get(id)).filter((t): t is BoxTruckTab => !!t)
            const leftovers = s.tabs.filter((t) => !orderedIds.includes(t.id))
            const next = [...reordered, ...leftovers]
            return next.length === s.tabs.length ? { ...s, tabs: next } : s
        })
    }, [])

    /** Rename a tab (auto on driver-select, or the operator's manual edit). An all-whitespace name clears the
     *  custom label so the tab falls back to labelling itself by its lane. */
    const renameTab = useCallback((id: string, label: string) => {
        const trimmed = label.trim()
        setState((s) => ({ ...s, tabs: s.tabs.map((t) => (t.id === id ? { ...t, label: trimmed || undefined } : t)) }))
    }, [])

    const commitFilters = useCallback((filters: BoxTruckSearchFilters, markSearched: boolean) => {
        setState((s) => ({
            ...s,
            tabs: s.tabs.map((tab) => (tab.id === s.activeTabId ? { ...tab, filters, searched: markSearched || tab.searched } : tab)),
        }))
    }, [])

    return {
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        activeTab,
        addTab,
        removeTab,
        closeAll,
        switchTab,
        commitFilters,
        reorderTabs,
        renameTab,
    }
}
